<template>
    <PopoverRoot :open="open" @update:open="onOpenChange">
        <PopoverTrigger as-child>
            <button
                ref="trigger"
                type="button"
                class="person-mention"
                :class="`person-mention-${variant}`"
                :data-variant="variant"
                data-testid="person-mention"
                @pointerenter="onTriggerPointerEnter"
                @pointerleave="onPointerLeave"
                @click.capture="onTriggerActivate">
                {{ label }}
            </button>
        </PopoverTrigger>
        <PopoverPortal>
            <PopoverContent
                ref="content"
                side="bottom"
                :align="align"
                :side-offset="8"
                :collision-padding="12"
                class="person-mention-card z-30"
                data-testid="person-mention-card"
                data-person-mention-card
                @pointerenter="cancelHide"
                @pointerleave="onPointerLeave"
                @interact-outside="onInteractOutside"
                @focusin="openedBy = 'click'"
                @open-auto-focus="keepFocusOnHover"
                @close-auto-focus="keepFocusOnHover">
                <div class="person-mention-card-body">
                    <slot />
                </div>
            </PopoverContent>
        </PopoverPortal>
    </PopoverRoot>
</template>

<script setup lang="ts">
import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";
import { onBeforeUnmount, ref, useTemplateRef } from "vue";

// linked = linked profile; suggested = added without a profile; unmatched = awaiting Add.
export type PersonMentionVariant = "linked" | "suggested" | "unmatched";

type Props = {
    label: string;
    variant: PersonMentionVariant;
    align?: "start" | "end";
};

const { label, variant, align = "start" } = defineProps<Props>();

// Long enough for the pointer to travel from the pill into the card without it closing.
const HIDE_DELAY_MS = 320;

const open = ref(false);
const openedBy = ref<"hover" | "click">("hover");
const trigger = useTemplateRef<HTMLButtonElement>("trigger");
const content = useTemplateRef<{ $el: HTMLElement }>("content");
let hideTimer: ReturnType<typeof setTimeout> | undefined;
let activatingTrigger = false;

const cancelHide = () => clearTimeout(hideTimer);

// A hover preview must not cover another person's card the user is working in; a click still switches.
const focusIsInAnotherCard = () => {
    const engagedCard = document.activeElement?.closest("[data-person-mention-card]");
    return !!engagedCard && engagedCard !== content.value?.$el;
};

const openOnHover = () => {
    cancelHide();
    if (!open.value && !focusIsInAnotherCard()) {
        openedBy.value = "hover";
        open.value = true;
    }
};

// Someone typing into the card (the add-details form, the profile search) keeps it open.
const hasFocusInside = () => !!content.value?.$el?.contains?.(document.activeElement);

const scheduleHide = () => {
    cancelHide();
    hideTimer = setTimeout(() => {
        if (openedBy.value === "hover" && !hasFocusInside()) {
            open.value = false;
        }
    }, HIDE_DELAY_MS);
};

const onTriggerPointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== "touch") {
        openOnHover();
    }
};

const onTriggerActivate = () => {
    activatingTrigger = true;
};

const onPointerLeave = (event: PointerEvent) => {
    if (event.pointerType !== "touch" && open.value) {
        scheduleHide();
    }
};

const onOpenChange = (value: boolean) => {
    cancelHide();
    const pinsHoverCard = !value && activatingTrigger && openedBy.value === "hover";
    activatingTrigger = false;
    if (pinsHoverCard) {
        openedBy.value = "click";
        // Autofocus only runs on mount; activating an already visible preview must move focus explicitly.
        content.value?.$el.querySelector<HTMLElement>('[role="dialog"]')?.focus();
        return;
    }
    if (value) {
        openedBy.value = "click";
    }
    open.value = value;
};

// Reka counts a press on the pill as an outside interaction, which would stop it returning focus to the pill on close.
const onInteractOutside = (event: Event) => {
    if (event.target instanceof Node && trigger.value?.contains(event.target)) {
        event.preventDefault();
    }
};

// A hover preview must not pull focus out of the composer, or hand it to the pill when it closes.
const keepFocusOnHover = (event: Event) => {
    if (openedBy.value === "hover") {
        event.preventDefault();
    }
};

onBeforeUnmount(cancelHide);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Every variant keeps the same 1px border so they are the same size and toggling causes no reflow. */
    .person-mention {
        @apply scaffold-body-small inline-block cursor-pointer rounded-full border px-2.5 align-baseline max-w-full leading-[1.35] wrap-break-word;
    }

    .person-mention:focus-visible {
        @apply outline-2 outline-offset-2 outline-person-mention-text;
    }

    /* Linked: solid purple fill (border matches fill so it stays the same size as the outline variants). */
    .person-mention-linked {
        @apply person-mention-linked-paint border-person-mention-fill;
    }

    /* The fill keeps the pill readable on tinted message bubbles. Border fades to half strength
       until hovered, so a resting pill reads lighter than the one under the pointer. */
    .person-mention-suggested {
        @apply border-control-border-strong/30 bg-surface text-ink transition-colors hover:border-control-border-strong;
    }

    /* A new detection stays dashed until the user adds the person. */
    .person-mention-unmatched {
        @apply border-dashed border-control-border-strong/50 bg-surface text-ink transition-colors hover:border-control-border-strong;
    }

    /* reka-ui's PopperContent forces `min-width: max-content` inline, which overrides a width set on
       the content itself and lets the card grow to fit its widest state. Giving the body a definite
       width makes that max-content resolve to a fixed number, so every state is the same width. */
    .person-mention-card-body {
        @apply w-85 max-w-[calc(100vw-24px)];
    }
}
</style>
